import React, { useCallback } from 'react';
import tw, { styled, TwStyle } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { GlobeAltIcon } from '@heroicons/react/24/outline';
import { ButtonV2, showToast } from '@restworld/ui-ds';
import { BASE_URL, copyToClipboard, customImageLoader, getAssetSrc, useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { useEmployerRestaurantDetails, useRestaurantNotPublicFlag } from '../../../hooks';
import Image from 'next/image';
import { composePDFOptions, RestaurantShowCaseQRA4 } from './restaurant-qr';
import { usePDF } from 'react-to-pdf';
import { Restaurant } from '@restworld/utility-types';

interface Props {
  slug?: string;
  variant?: 'aligned' | 'pyramid'; // show the customize card on top and the other below
  containerTw?: TwStyle;
  inputRestaurantDetails?: Restaurant;
}

export default function ShowcaseNextSteps({
  slug: inputSlug,
  inputRestaurantDetails,
  variant = 'aligned',
  containerTw,
}: Props) {
  const { t } = useTranslation('restaurant-details-page');
  const router = useRouter();
  const { trackPHEvent } = useAnalytics();
  const { user } = useAuth();
  const slug = (router.query.slug as string) || inputSlug;
  const restaurantDetailsFromAPI = useEmployerRestaurantDetails(slug);
  const isShowcaseOfflineFromAPI = useRestaurantNotPublicFlag(slug);
  const restaurantDetails = inputRestaurantDetails || restaurantDetailsFromAPI;
  const isShowcaseOffline = inputRestaurantDetails ? !inputRestaurantDetails.show_details : isShowcaseOfflineFromAPI;

  const pdfOptions = composePDFOptions(slug);
  const { toPDF, targetRef } = usePDF(pdfOptions);

  const handleCustomizeShowcase = useCallback(() => {
    router.push(`/employer/venues/${slug}/information`);
  }, [router, slug]);

  const handleShareOnSocial = useCallback(() => {
    const showcasePageUrl = `${BASE_URL}/locale/${slug}`;

    trackPHEvent('employer_share_social_media', {
      email: user?.email,
      restaurant_name: restaurantDetails?.restaurant_name,
      open_jps: restaurantDetails?.open_job_positions?.length,
    });

    copyToClipboard(showcasePageUrl).then((isSuccess) => {
      isSuccess &&
        showToast(t('link_copied_in_the_notes'), 'success', {
          position: 'top-right',
          autoClose: 3000,
        });
    });
  }, [slug, t, trackPHEvent, user?.email, restaurantDetails]);

  const handleDownloadQR = useCallback(() => {
    trackPHEvent('employer_click_qr_code', { email: user?.email, restaurant_name: slug });
    toPDF();
  }, [slug, toPDF, trackPHEvent, user?.email]);

  if (isShowcaseOffline) {
    return (
      <Container $tw={containerTw}>
        <OfflineContainer>
          <OfflineHeader>
            <OfflineTitle>{t('want_to_receive_more_spontaneous_applications')}</OfflineTitle>
            <OfflineSubtitle>
              {t('your_showcase_is_still_offline')} {t('publish_it_to_get_known_by_restworld_community')}
            </OfflineSubtitle>
          </OfflineHeader>
          <OfflineCard>
            <OfflineCardContent>
              <IconContainer>
                <EmojiIcon>🖌️</EmojiIcon>
              </IconContainer>
              <ButtonV2 StartIcon={GlobeAltIcon} onClick={handleCustomizeShowcase} twStyle={tw`w-full`}>
                {t('customize_and_publish')}
              </ButtonV2>
              <OfflineDescription>
                {t('fill_all_fields_to_present_yourself_best_and_attract_best_talents')}
              </OfflineDescription>
            </OfflineCardContent>
          </OfflineCard>
        </OfflineContainer>
      </Container>
    );
  }

  const items = [
    <CardItem
      icon={<EmojiIcon>🖌️</EmojiIcon>}
      description={t('show_your_style_values_and_atmosphere')}
      ctaLabel={t('customize_your_showcase')}
      handleClick={handleCustomizeShowcase}
      key={0}
    />,
    <CardItem
      icon={<EmojiIcon>🔗</EmojiIcon>}
      description={t('share_the_link_on_socials_and_gather_candidates_from_your_followers')}
      ctaLabel={t('share_showcase_on_social')}
      handleClick={handleShareOnSocial}
      key={1}
    />,
    <CardItem
      icon={
        <Image
          src={getAssetSrc('/utils-assets/print_qr.png')}
          width={64}
          height={64}
          alt="QR Code"
          loader={customImageLoader}
          tw="object-contain"
        />
      }
      description={t('hang_the_qr_on_the_door_and_counter_and_receive_digital_applications')}
      ctaLabel={t('download_and_hang_window_sticker')}
      handleClick={handleDownloadQR}
      key={2}
    />,
  ];

  return (
    <Container $tw={containerTw}>
      <Title>{t('want_to_receive_more_spontaneous_applications')}</Title>
      {variant === 'aligned' && <CardsContainer>{items.map((item) => item)}</CardsContainer>}
      {variant === 'pyramid' && (
        <div tw="flex flex-col gap-4 w-full">
          <div tw="w-full">{items[0]}</div>
          <div tw="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">{items.slice(1)}</div>
        </div>
      )}
      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
        <RestaurantShowCaseQRA4 ref={targetRef} restaurantDetailsQR={restaurantDetails?.qr_code_url} />
      </div>
    </Container>
  );
}

const CardItem = ({
  icon,
  description,
  handleClick,
  ctaLabel,
}: {
  icon: React.ReactNode;
  description: string;
  ctaLabel: string;
  handleClick: VoidFunction;
}) => (
  <Card>
    <CardContent>
      <IconContainer>{icon}</IconContainer>
      <Description>{description}</Description>
    </CardContent>
    <ButtonV2 onClick={handleClick} tw="w-full">
      {ctaLabel}
    </ButtonV2>
  </Card>
);

const Container = styled.div<{ $tw?: TwStyle }>(({ $tw }) => [
  tw`flex flex-col items-start gap-6 self-stretch bg-transparent rounded-2xl p-6 lg:p-8`,
  $tw,
]);

const Title = styled.h2`
  ${tw`text-center text-neutral-700 font-semibold text-lg lg:text-xl w-full`}
`;

const CardsContainer = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 self-stretch w-full`}
`;

const Card = styled.div`
  ${tw`flex flex-col justify-between items-center gap-6 flex-1 p-6 rounded-lg border border-purple-700 bg-purple-50`}
`;

const CardContent = styled.div`
  ${tw`flex flex-col justify-between items-center gap-4 self-stretch`}
`;

const IconContainer = styled.div`
  ${tw`flex w-20 h-20 flex-col justify-center items-center rounded-2xl`}
  background: rgba(102, 51, 187, 0.10);
  box-shadow: 4px 4px 0 0 rgba(72, 36, 133, 0.5);
`;

const EmojiIcon = styled.div`
  ${tw`text-4xl text-center`}
`;

const Description = styled.p`
  ${tw`text-neutral-700 text-center font-normal text-base w-full`}
`;

const OfflineContainer = styled.div`
  ${tw`flex flex-col items-center gap-4 lg:gap-6 self-stretch rounded-2xl border border-purple-700 bg-purple-50 py-10 lg:py-12 px-6`}
`;

const OfflineHeader = styled.div`
  ${tw`flex flex-col items-center gap-3 w-full max-w-[480px]`}
`;

const OfflineTitle = styled.h2`
  ${tw`text-center text-purple-700 font-semibold text-lg lg:text-2xl w-full`}
`;

const OfflineSubtitle = styled.p`
  ${tw`text-center text-neutral-700 font-normal text-base w-full`}
`;

const OfflineCard = styled.div`
  ${tw`flex flex-col items-center w-full max-w-[200px]`}
`;

const OfflineCardContent = styled.div`
  ${tw`flex flex-col items-center gap-5 w-full xs:min-w-[300px]`}
`;

const OfflineDescription = styled.p`
  ${tw`text-center text-neutral-700 font-normal text-base w-full`}
`;
